import React from "react";
import classNames from "classnames";
import { Control, Controller, useForm } from "react-hook-form";
import { ClientInfoForm } from "features/ClientInfo/ClientInfo";
import {
  useAddClientDate,
  useDeleteClientDate,
  useGetClientDate,
} from "features/ClientInfo/api";
import { useClientInfo, useClientsState } from "atoms/clientsAtom";
import { AddClientDateParams } from "features/ClientInfo/types";
import { ClientDate } from "types";
import moment from "moment";

import { NamedCounter } from "components/NamedCounter/NamedCounter";
import { MediumText } from "components/MediumText/MediumText";
import { VerticalPadding } from "components/VerticalPadding/VerticalPadding";
import { Row } from "components/Row/Row";
import { Divider } from "components/Divider/Divider";
import { Button, EButtonVariant } from "components/Button/Button";
import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";

import DaysServices from "utils/days";

import styles from "../../styles.module.css";

import { TableRow } from "./TableRow";
import { AddFieldsRow } from "./AddFieldsRow";

type Props = {
  control: Control<ClientInfoForm>;
  isEdit?: boolean;
};

const FIELDS = [
  { varName: "id", text: "№", width: "22px" },
  { varName: "name", text: "Название", width: "132px" },
  { varName: "date", text: "Дата", width: "125px" },
  { varName: "bonus", text: "Бонус", width: "60px" },
  { varName: "plusbonus", text: "Увеличить % бонуса", width: "150px" },
  { varName: "pluspercent", text: "Скидка %", width: "70px" },
  { varName: "radiusminus", text: "Действуют с оплатой, дни", width: "240px" },
  { varName: "burnday", text: "Сгорают", width: "52px" },
  { varName: "useonce", text: "Начисляются 1 раз", width: "130px" },
  { varName: "domessage", text: "Рассылка", width: "60px" },
  { varName: "smstextid", text: "Шаблон СМС", width: "166px" },
  { varName: "rememberday", text: "Напоминать о дате", width: "145px" },
];

export type ClientInfoMarketing = Omit<AddClientDateParams, "clientid">;

export const Marketing = ({ control, isEdit }: Props): JSX.Element => {
  const { clientsState } = useClientsState();
  const { clientInfoState, setClientInfoState } = useClientInfo();

  const { data: clientDates = [] } = useGetClientDate(
    clientsState.activeItem?.clientid || ""
  );

  const { mutate: deleteClientDate } = useDeleteClientDate();

  const { mutate: addClientDate } = useAddClientDate();

  const {
    handleSubmit,
    control: marketingControl,
    reset,
    watch,
  } = useForm<ClientInfoMarketing>({
    defaultValues: {
      name: "",
      date: "",
      bonus: "",
      plusbonus: "",
      pluspercent: "",
      radiusminus: "",
      radiusplus: "",
      burnday: 0,
      useonce: 0,
      domessage: 0,
      smstextid: "",
      rememberday: "",
    },
  });

  const allValues = watch();

  const handleAddClick = () => {
    setClientInfoState((prev) => ({
      ...prev,
      showAddMarketingFields: true,
    }));
  };

  const handleSaveClick = (data: ClientInfoMarketing) => {
    const differenceInHours = moment().utcOffset() / 60;
    addClientDate(
      {
        ...data,
        date: moment(data.date).add(differenceInHours, "hours").toISOString(),
        clientid: clientsState.activeItem?.clientid || "",
        burnday: data.burnday ? 1 : 0,
        useonce: data.useonce ? 1 : 0,
        domessage: data.domessage ? 1 : 0,
        smstextid: data.smstextid || "1",
        plusdiscount: data.pluspercent,
      },
      { onSuccess: () => reset() }
    );
  };

  const handleDeleteClick = () => {
    if (clientInfoState.selectedMarketingId) {
      deleteClientDate(clientInfoState.selectedMarketingId);
    }
  };

  return (
    <div className="w-full flex justify-between py-6 px-4  flex-wrap">
      <div>
        <h4 className={styles.marketing_title}>Букет в подарок</h4>
        <div className="flex items-center gap-x-2">
          <span className={styles.marketing_text}>Скидка</span>
          <Controller
            name="eachbouquetdiscount"
            control={control}
            render={({ field }) => <NamedCounter {...field} title="" />}
          />
          <span className={styles.marketing_text}>на каждый</span>
          <Controller
            name="eachbouquet"
            control={control}
            render={({ field }) => <NamedCounter {...field} title="" />}
          />
          <span className={styles.marketing_text}>букет</span>
        </div>
      </div>
      <div>
        <h4 className={styles.marketing_title}>Сгорание бонусов</h4>
        <div className="flex items-center gap-x-2">
          <span className={styles.marketing_text}>Бонусы сгорают после</span>
          <Controller
            name="defaultburnbonus"
            control={control}
            render={({ field }) => <NamedCounter {...field} title="" />}
          />
          <span className={styles.marketing_text}>дней после начисления</span>
        </div>
      </div>

      {isEdit && (
        <div className={classNames(styles.container, "w-full" as any)}>
          <MediumText size={16}>Знаменательные даты</MediumText>
          <VerticalSpace size={30} />
          <div className={styles.table}>
            <Row className={styles.row}>
              <div className="flex flex-1 gap-6 items-center">
                {FIELDS.map((tempField) => (
                  <MediumText
                    key={tempField.varName}
                    style={{
                      width: tempField.width,
                      minWidth: tempField.width,
                    }}
                    className={styles.table_head_column}
                  >
                    {tempField.text}
                  </MediumText>
                ))}
              </div>
            </Row>
            <Divider width={1616} />
            {clientDates.map((clientDate: ClientDate) => (
              <TableRow key={clientDate.clientdateid} clientDate={clientDate} />
            ))}
            {clientInfoState.showAddMarketingFields && (
              <AddFieldsRow control={marketingControl} />
            )}
          </div>
          <VerticalSpace size={30} />
          <div className="flex items-center gap-2">
            {clientInfoState.showAddMarketingFields ? (
              <Button
                variant={EButtonVariant.SECONDARY}
                onClick={handleSubmit(handleSaveClick)}
                icon="galochka"
              >
                Сохранить
              </Button>
            ) : (
              <Button
                variant={EButtonVariant.SECONDARY}
                onClick={handleAddClick}
                icon="plus"
              >
                Добавить
              </Button>
            )}

            <Button
              variant={EButtonVariant.PRIMARY}
              onClick={handleDeleteClick}
              icon="delete"
            >
              Удалить
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
